import { useMutation } from '@tanstack/vue-query'

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { sortTaskByPriority } from '@/store/user-store/utils'
import type { GameAttributes, InstreamData, TaskDto, User, UserSocialsType } from '@/types'
import { useAPI } from '@/utils/requests/api-plugin'

import { useLayoutStore } from '../layout-store'

export type ExportApi = {
    authorizeUser: UserStore['authorizeUser']
    getUser: UserStore['getUser']
    chargeUser: UserStore['chargeUser']
    cancelCharge: UserStore['cancelCharge']
    closeGame: () => void
    getGameAttributes: () => GameAttributes
    updateInstreamData: (data: Partial<InstreamData>) => void
    openDisabledAdModal: () => void
}

const DEFAULT_COINS = 1000

export const useUserStore = defineStore('user', () => {
    const user = ref<User | null>(null)
    const api = useAPI()
    const userLoadingPromise = ref<Promise<User | null> | null>(null)
    const isUserInfoLoaded = ref(false)
    const isAuthorized = ref<boolean>(false)
    const tasks = ref<TaskDto[]>([])
    const tasksError = ref<boolean>(false)
    const userBalance = ref<number>(0)

    const isCompanyEmployeeLoggedIn = computed(() => {
        return !!user.value?.email?.endsWith('@playgama.com')
    })

    const layoutStore = useLayoutStore()

    function fetchUserInfo(): Promise<User | null> {
        isUserInfoLoaded.value = false
        userLoadingPromise.value = Promise.resolve().then(async () => {
            let data
            if (typeof window !== 'undefined') {
                data = await api.getCurrentUser()
            }
            isUserInfoLoaded.value = true
            if (!data) {
                isAuthorized.value = false
                user.value = null
                return null
            }

            isAuthorized.value = true
            user.value = data
            return data
        })

        return userLoadingPromise.value
    }

    function getUser(): Promise<User | null> {
        if (userLoadingPromise.value) {
            return userLoadingPromise.value
        }

        if (typeof window !== 'undefined' && window?.additionalServerData?.hasToken !== false) {
            userLoadingPromise.value = fetchUserInfo()
        } else {
            userLoadingPromise.value = Promise.resolve(null)
            isUserInfoLoaded.value = true
        }

        return userLoadingPromise.value
    }

    async function authorizeUser(): ReturnType<typeof getUser> {
        await getUser()
        if (!user.value) {
            await new Promise<void>((resolve) => {
                layoutStore.setSignInModalOpened(true)
                layoutStore.$subscribe((mutation, state) => {
                    if (mutation.type === 'direct' && !state.signInModalOpened) {
                        fetchUserInfo().then(() => resolve())
                    }
                })
            })
        }

        if (user.value) {
            return { ...user.value }
        }

        throw new Error("User didn't complete login")
    }

    async function getUserTasks() {
        try {
            const data = await api.getTasks()
            tasksError.value = false
            tasks.value = sortTaskByPriority(data.tasks)
            userBalance.value = data.wallet.coins
        } catch {
            tasksError.value = true
            tasks.value = []
            userBalance.value = DEFAULT_COINS
        }
    }

    async function claimUserTasks(taskId: string): Promise<void> {
        const oldUserBalance = userBalance.value
        const oldTasksData = tasks.value

        userBalance.value += tasks.value.find(({ task_id }) => task_id === taskId)!.coins
        tasks.value = tasks.value.map((task) => {
            if (task.task_id === taskId) {
                return { ...task, status: 'CLAIMED' }
            }
            return task
        })

        try {
            const data = await api.claimTask(taskId)
            tasks.value = sortTaskByPriority(data.tasks)
            userBalance.value = data.wallet.coins
        } catch {
            tasks.value = oldTasksData
            userBalance.value = oldUserBalance
            tasksError.value = true
        }
    }

    async function logout() {
        userLoadingPromise.value = Promise.resolve(null)
        await api.request('/api/v1/auth/logout', { method: 'post' })
        user.value = null
        isAuthorized.value = false
        tasks.value = []
    }

    async function updateSocials({ key, account }: UserSocialsType) {
        const { originalResponse } = await api.request('/api/v1/user', {
            method: 'post',
            body: JSON.stringify({
                [key]: account,
            }),
        })
        if (originalResponse.ok) {
            user.value![key] = account
        } else {
            // ???
        }
    }

    function cancelCharge(reason: string) {
        layoutStore.paymentModalOrder?.reject?.(reason)

        layoutStore.setPaymentModalOrder(null)
    }

    const useMutationUsersGame = useMutation({
        mutationKey: ['user', 'my-games', 'update'],
        mutationFn: (hru: string) => {
            return api.request('/api/v1/user/my-games', {
                method: 'post',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ hru }),
            })
        },
        onError: () => {
            console.error('Failed to update users game')
        },
    })
    function updateUsersGame(hru: string) {
        if (!user.value) {
            return
        }
        useMutationUsersGame.mutateAsync(hru)
    }

    function setUser(data: User | null) {
        user.value = data
        isAuthorized.value = true
        isUserInfoLoaded.value = true
    }

    return {
        user,
        isAuthorized,
        isCompanyEmployeeLoggedIn,
        isUserInfoLoaded,
        getUser,
        getUserTasks,
        tasks,
        logout,
        updateSocials,
        tasksError,
        userBalance,
        claimUserTasks,
        authorizeUser,
        fetchUserInfo,
        cancelCharge,
        updateUsersGame,
        chargeUser: layoutStore.setPaymentModalOrder,
        setUser,
    }
})

export type UserStore = ReturnType<typeof useUserStore>
